import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { queryKeys } from '@/shared/lib/query/queryKeys';
import { getErrorMessage } from '@/shared/api/errors';
import { normalizeWalletListResponse } from '../utils/normalizeWalletListResponse';
import type { Settlement, Txn } from '../pages/WalletPage/WalletPage.types';
import * as api from './walletApi';

export function useWalletStatsQuery() {
  return useQuery({
    queryKey: queryKeys.wallet.balance(),
    queryFn: api.fetchWalletStats,
    refetchOnWindowFocus: false,
  });
}

export function useTransactionsQuery(filter: Record<string, unknown>) {
  const page = Number(filter.page) || 1;
  const limit = Number(filter.limit) || 10;

  return useQuery({
    queryKey: [...queryKeys.wallet.root, 'transactions', filter],
    queryFn: async () => {
      const raw = await api.fetchTransactions(filter);
      return normalizeWalletListResponse<Txn>(raw, { page, limit }, [
        'transactions',
        'data',
        'list',
      ]);
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });
}

export function useWalletChartQuery(
  startDate: string,
  endDate: string,
  reason?: string,
) {
  return useQuery({
    queryKey: [
      ...queryKeys.wallet.root,
      'chart',
      startDate,
      endDate,
      reason || '',
    ],
    queryFn: () => api.fetchWalletChart({ startDate, endDate, reason }),
    enabled: !!startDate && !!endDate,
    refetchOnWindowFocus: false,
  });
}

export function useSettlementsQuery(filter: Record<string, unknown>) {
  const page = Number(filter.page) || 1;
  const limit = Number(filter.limit) || 10;

  return useQuery({
    queryKey: [...queryKeys.wallet.root, 'settlements', filter],
    queryFn: async () => {
      const raw = await api.fetchSettlements(filter);
      return normalizeWalletListResponse<Settlement>(raw, { page, limit }, [
        'settlements',
        'data',
        'list',
      ]);
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });
}

export function useBankDetailsQuery(enabled = true) {
  return useQuery({
    queryKey: [...queryKeys.wallet.root, 'bank-details'],
    queryFn: api.fetchBankDetails,
    enabled,
    refetchOnWindowFocus: false,
  });
}

export function useSearchUsersQuery(email: string) {
  return useQuery({
    queryKey: [...queryKeys.wallet.root, 'users', email],
    queryFn: () => api.searchUsersByEmail(email),
    enabled: email.includes('@') && email.length > 3,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useDownloadCsvMutation() {
  return useMutation({
    mutationFn: api.downloadTransactionsCsv,
    onSuccess: (blob: Blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Transactions_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Failed to export CSV'));
    },
  });
}

export function useInvoiceMutation() {
  return useMutation({
    mutationFn: ({ id, type }: { id: string; type: string }) =>
      api.fetchInvoice(id, type),
    onSuccess: (blob: Blob, variables) => {
      const pdfBlob = new Blob([blob], { type: 'application/pdf' });
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice_${variables.id}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Invoice downloaded');
    },
    onError: async (error) => {
      let message = 'Failed to download invoice';
      const axiosErr = error as {
        response?: { data?: unknown };
        message?: string;
      };
      const data = axiosErr.response?.data;
      if (typeof Blob !== 'undefined' && data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text) as { message?: string };
          if (parsed?.message) message = parsed.message;
        } catch {
          message = getErrorMessage(error, message);
        }
      } else {
        message = getErrorMessage(error, message);
      }
      toast.error(message);
    },
  });
}

function invalidateWallet(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: queryKeys.wallet.root });
}

export function useDepositMutation() {
  return useMutation({
    mutationFn: api.depositAmount,
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Deposit failed'));
    },
  });
}

export function useDepositChargesMutation() {
  return useMutation({
    mutationFn: api.fetchDepositCharges,
  });
}

export function useVerifyPaymentMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.verifyPayment,
    onSuccess: (data) => {
      toast.success((data as { message?: string })?.message || 'Payment verified');
      invalidateWallet(qc);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Payment verification failed'));
    },
  });
}

export function useAddBankMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.addBankDetails,
    onSuccess: (data) => {
      toast.success((data as { message?: string })?.message || 'Bank added');
      invalidateWallet(qc);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Failed to add bank'));
    },
  });
}

export function useDeleteBankMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.deleteBankDetails,
    onSuccess: () => {
      toast.success('Bank account removed');
      invalidateWallet(qc);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Failed to delete bank'));
    },
  });
}

export function useWithdrawalMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.requestWithdrawal,
    onSuccess: (data) => {
      toast.success(
        (data as { message?: string })?.message || 'Withdrawal requested',
      );
      invalidateWallet(qc);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Withdrawal failed'));
    },
  });
}

export function useWithdrawDeductionMutation() {
  return useMutation({
    mutationFn: api.fetchWithdrawDeduction,
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Could not calculate deductions'));
    },
  });
}

export function useTransferChargesMutation() {
  return useMutation({
    mutationFn: api.fetchTransferCharges,
  });
}

export function useTransferQndtMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.transferQndt,
    onSuccess: (data) => {
      toast.success(
        (data as { message?: string })?.message || 'Transfer successful',
      );
      invalidateWallet(qc);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Transfer failed'));
    },
  });
}
